import { useCallback, useEffect, useState } from "react";
import type { Aggregate } from "../lib/cellSelection";

interface Props {
  aggregate: Aggregate | null;
  onClear: () => void;
  copied: boolean;
}

// Excel-style aggregate readout for a cell selection. Renders nothing when nothing is
// selected, so the grid's resting layout is untouched.
//
// Sum and Count lead because footing a subset is the actual task; Min/Max follow
// because an unmatched bucket's difference is usually driven by one or two large
// items, and the spread tells the user whether they are chasing one big row or many
// small ones. A negative Min in a column expected to be all-debit flags reversals in
// the selection.
const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SelectionStatusBar({ aggregate, onClear, copied }: Props) {
  // Which stat just went to the clipboard, so the tick sits on the one that was clicked.
  const [hit, setHit] = useState<string | null>(null);
  useEffect(() => { setHit(null); }, [aggregate]);

  // Copy the RAW number, not the display string: Excel parses "1234.5" as a number but
  // takes a grouped "1,234.50" as text in several locales.
  const copyStat = useCallback((label: string, raw: number) => {
    navigator.clipboard?.writeText(String(raw)).then(
      () => { setHit(label); window.setTimeout(() => setHit((h) => (h === label ? null : h)), 1600); },
      () => { /* clipboard blocked (insecure origin) — selection stays, no toast */ },
    );
  }, []);

  if (!aggregate) return null;
  const { count, sum, avg, min, max, nonNumeric } = aggregate;

  const stat = (label: string, raw: number, shown: string, lead = false) => (
    <button
      type="button"
      className={"sel-stat sel-stat--copy" + (lead ? " sel-stat--lead" : "") + (hit === label ? " sel-stat--hit" : "")}
      onClick={() => copyStat(label, raw)}
      title={`Copy ${label} to clipboard`}
      aria-label={`Copy ${label} ${shown} to clipboard`}
    >
      <span className="sel-stat-label">{label}</span>
      <span className="sel-stat-value">{shown}</span>
      <span className="sel-stat-tick" aria-hidden="true">{hit === label ? "✓" : ""}</span>
    </button>
  );

  return (
    <div className="sel-status" role="status" aria-live="polite">
      {count > 0 ? (
        <>
          {stat("Sum", sum, fmt(sum), true)}
          {stat("Count", count, count.toLocaleString(), true)}
          {stat("Average", avg, fmt(avg))}
          {stat("Min", min, fmt(min))}
          {stat("Max", max, fmt(max))}
        </>
      ) : (
        <span className="sel-stat">
          <span className="sel-stat-label">No numeric cells selected</span>
        </span>
      )}
      {nonNumeric > 0 && count > 0 && (
        <span className="sel-stat sel-stat--muted">
          {nonNumeric.toLocaleString()} text cell{nonNumeric === 1 ? "" : "s"} ignored
        </span>
      )}
      <span className="sel-status-spacer" />
      {copied && <span className="sel-stat sel-stat--ok">Copied</span>}
      <span className="sel-stat sel-stat--muted sel-hint">Click a stat to copy · Ctrl+C for cells</span>
      <button className="sel-clear" onClick={onClear} title="Clear selection" aria-label="Clear selection">✕</button>
    </div>
  );
}
